import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useLanguage } from '@/contexts/LanguageContext';
import HeaderSummary from './HeaderSummary';
import Performance3DCharts from './Performance3DCharts';
import BenchmarkTable from './BenchmarkTable';
import Risk3DRadar from './Risk3DRadar';
import { ScoreBreakdown } from '@/types/survey';
import { 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip as RechartsTooltip, 
  ResponsiveContainer 
} from 'recharts';
import { Button } from '@/components/ui/button';
import { Download, CheckCircle2, Loader2 } from 'lucide-react';
import { generatePdf } from '@/utils/pdfGenerator';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

interface ProfessionalDashboardProps {
  nevScore?: number;
  rating?: 'High Risk' | 'Competitive' | 'Exceptional';
  eroi?: number;
  metrics?: {
    ea: number;
    elv: number;
    l: number;
    tb: number;
    bc: number;
  };
  industryAverages?: {
    ea: number;
    elv: number;
    l: number;
    tb: number;
    bc: number;
  };
  recommendations?: string[];
  scoreBreakdown?: ScoreBreakdown;
  industry?: string;
}

const ProfessionalDashboard: React.FC<ProfessionalDashboardProps> = ({
  nevScore,
  rating,
  eroi,
  metrics,
  industryAverages,
  recommendations,
  scoreBreakdown,
  industry = "Technology"
}) => {
  const { t, language } = useLanguage();
  const { toast } = useToast();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  
  const actualMetrics = metrics || (scoreBreakdown ? {
    ea: scoreBreakdown.ea.percentage / 100,
    elv: scoreBreakdown.elv.percentage / 100,
    l: scoreBreakdown.l.percentage / 100,
    tb: scoreBreakdown.tb.percentage / 100,
    bc: scoreBreakdown.bc.percentage / 100
  } : undefined);
  
  const actualNevScore = nevScore || (scoreBreakdown ? scoreBreakdown.totalNEV : 0);
  const actualRating = rating || (scoreBreakdown ? scoreBreakdown.rating : 'Competitive');
  const actualEroi = eroi || (scoreBreakdown ? scoreBreakdown.eroi : 1.0);
  
  const defaultIndustryAverages = {
    ea: 0.65,
    elv: 0.60,
    l: 0.70,
    tb: 0.68,
    bc: 0.72
  };
  
  const actualIndustryAverages = industryAverages || defaultIndustryAverages;
  
  const actualRecommendations = recommendations || 
    (scoreBreakdown && scoreBreakdown.recommendations && scoreBreakdown.recommendations.length > 0 
      ? scoreBreakdown.recommendations 
      : [
          'Strong emotional connection offset by ethical concerns. Focus on transparency.',
          'Solid ethical foundation but lacks emotional connection. Build community outreach.',
          'Long-term viability at risk despite current performance. Build resilience strategy.'
        ]);
  
  const recommendationComments = [
    language === 'zh' ? t('results.recommendationHighPriority') : 
      "This high-priority recommendation addresses the most critical aspect of your project's emotional value. Implementing this action will yield the highest return on emotional investment.",
    language === 'zh' ? t('results.recommendationMediumPriority') : 
      "This recommendation focuses on important aspects that will significantly enhance your project's connection with your audience.",
    language === 'zh' ? t('results.recommendationLowPriority') : 
      "This recommendation provides additional value and should be considered after addressing the higher priority items."
  ];
  
  if (!actualMetrics) {
    return (
      <div className="w-full p-8 text-center">
        <p className="text-muted-foreground">No metrics data available</p>
      </div>
    );
  }

  const riskData = {
    community: actualMetrics.ea * 10,
    ethics: actualMetrics.elv * 10,
    funding: actualMetrics.l * 10,
    innovation: actualMetrics.tb * 10
  };
  
  // Extract the chart data from score breakdown or use calculated values
  const chartData = {
    fandom: scoreBreakdown?.submetrics?.ea?.fandom || actualMetrics.ea * 10,
    trust: scoreBreakdown?.submetrics?.ea?.trust || actualMetrics.tb * 10,
    referrals: scoreBreakdown?.submetrics?.ea?.referrals || actualMetrics.bc * 10,
    redFlags: scoreBreakdown?.submetrics?.elv?.redFlags || ["Transparency concerns", "Market volatility"],
    brightSpots: scoreBreakdown?.submetrics?.elv?.brightSpots || ["Strong community", "Leadership vision"]
  };
  
  const handleDownloadPDF = () => {
    if (scoreBreakdown) {
      const projectName = "Your Project"; // This should be dynamically set based on actual project name
      generatePdf(scoreBreakdown, projectName, industry || "Technology", language);
    }
  };
  
  const handleWaitlistSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast({
        title: "Invalid email",
        description: "Please enter a valid email address.",
        variant: "destructive"
      });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      console.log("Submitting email to waitlist:", email);
      
      // Simple local storage fallback for when Supabase table doesn't exist
      const localWaitlist = localStorage.getItem('pro_waitlist') 
        ? JSON.parse(localStorage.getItem('pro_waitlist') || '[]') 
        : [];
      
      // Check if email already exists in local storage
      if (localWaitlist.some(item => item.email === email)) {
        toast({
          title: "Already subscribed",
          description: "This email is already on our waitlist.",
        });
        setIsSubscribed(true);
        setIsSubmitting(false);
        return;
      }
      
      try {
        // Try to insert into Supabase (this might fail if table doesn't exist)
        const { error } = await supabase
          .from('pro_waitlist')
          .insert([
            {
              email: email,
              industry: industry,
              signup_date: new Date().toISOString()
            }
          ]);
          
        if (error) {
          console.log("Supabase insertion error:", error.message);
          // If it's a duplicate email error
          if (error.code === '23505' || (error.message && error.message.includes('duplicate'))) {
            toast({
              title: "Already subscribed",
              description: "This email is already on our waitlist.",
            });
            setIsSubscribed(true);
            return;
          }
          
          // If table doesn't exist or other errors, use local storage as fallback
          throw error;
        }
      } catch (supabaseError) {
        console.log("Using local storage fallback due to Supabase error");
        
        // Store in local storage as fallback
        localWaitlist.push({
          email: email,
          industry: industry,
          signup_date: new Date().toISOString()
        });
        
        localStorage.setItem('pro_waitlist', JSON.stringify(localWaitlist));
      }
      
      setIsSubscribed(true);
      toast({
        title: "Success!",
        description: "You've been added to the BEAMEvaluate Pro waitlist.",
      });
      
      setEmail('');
    } catch (error) {
      console.error("Error submitting to waitlist:", error);
      toast({
        title: "Something went wrong",
        description: "We couldn't add you to the waitlist. Please try again later.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <div className="w-full space-y-8">
      <HeaderSummary 
        nevScore={actualNevScore}
        rating={actualRating}
        industryPercentile={75}
        keyInsight={scoreBreakdown?.keyInsight || actualRecommendations[0]}
      />
      
      <div className="flex justify-end">
        <Button 
          onClick={handleDownloadPDF} 
          className="bg-blue-700 text-white hover:bg-blue-800"
          size="sm"
        >
          <Download className="mr-2 h-4 w-4" />
          {language === 'zh' ? t('results.downloadReport') : 'Download PDF Report'}
        </Button>
      </div>
      
      {/* Changed to use our new 3D Performance Charts component */}
      <div className="space-y-6">
        <Performance3DCharts 
          fandom={chartData.fandom}
          trust={chartData.trust}
          referrals={chartData.referrals}
          redFlags={chartData.redFlags}
          brightSpots={chartData.brightSpots}
        />
        
        {/* Risk Radar now positioned directly under Performance Analysis */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">
              Risk Assessment
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Risk3DRadar data={riskData} />
          </CardContent>
        </Card>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Added NEV label to clarify the metric */}
        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg flex items-center">
              {language === 'zh' ? t('results.beamEvaluateAnalysis') : 'BEAMEvaluate Analysis'} 
              <span className="ml-2 text-sm font-normal bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                NEV (Net Emotional Value): {actualNevScore.toFixed(1)}/50
              </span>
            </CardTitle>
            <div className="text-xs text-blue-700 font-medium">
              {language === 'zh' ? t('results.advancedView') : 'Advanced View'}
            </div>
          </CardHeader>
          <CardContent>
            <ul className="space-y-6">
              {actualRecommendations.map((recommendation, index) => (
                <li key={index} className="flex flex-col space-y-2">
                  <div className="flex items-start gap-2">
                    <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border ${
                      index === 0 ? 'bg-red-100 text-red-700' : 
                      index === 1 ? 'bg-amber-100 text-amber-700' : 
                      'bg-green-100 text-green-700'
                    } text-xs`}>
                      {index + 1}
                    </span>
                    <span className="text-sm font-medium text-gray-800">
                      {recommendation}
                    </span>
                  </div>
                  {index < recommendationComments.length && (
                    <p className="text-xs text-gray-600 ml-8">
                      {recommendationComments[index]}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
        
        {/* Add AI & Blockchain features explanation with Coming Soon and waitlist */}
        <Card className="lg:col-span-2 bg-gradient-to-r from-blue-50 to-purple-50">
          <CardHeader className="relative">
            <div className="absolute right-6 top-6">
              <span className="bg-blue-600 text-white text-xs px-3 py-1 rounded-full font-medium">
                Coming Q4 2025
              </span>
            </div>
            <CardTitle className="text-lg text-blue-800">
              BEAMEvaluate Pro: AI & Blockchain Integration
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <h4 className="font-semibold text-blue-700 mb-2">AI-Powered Analysis</h4>
                <ul className="space-y-2 text-sm">
                  <li className="flex gap-2">
                    <span className="text-blue-500">•</span>
                    <span>Advanced sentiment analysis of all survey responses</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-blue-500">•</span>
                    <span>Predictive modeling for future brand performance</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-blue-500">•</span>
                    <span>Automated pattern recognition across industry datasets</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-blue-500">•</span>
                    <span>Personalized strategic recommendations</span>
                  </li>
                </ul>
              </div>
              
              <div>
                <h4 className="font-semibold text-purple-700 mb-2">Blockchain Integration</h4>
                <ul className="space-y-2 text-sm">
                  <li className="flex gap-2">
                    <span className="text-purple-500">•</span>
                    <span>Immutable record of all brand evaluation data</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-purple-500">•</span>
                    <span>Verifiable audit trail of brand equity development</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-purple-500">•</span>
                    <span>Secure, tamper-proof data storage</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-purple-500">•</span>
                    <span>Transparent competitive benchmarking</span>
                  </li>
                </ul>
              </div>
            </div>
            
            <div className="mt-6">
              <div className="bg-white p-4 rounded-lg shadow-sm">
                <h4 className="font-semibold text-gray-800 mb-2">Join the Waitlist</h4>
                <p className="text-sm text-gray-600 mb-4">
                  Be the first to know when BEAMEvaluate Pro is available. Get early access and exclusive pricing.
                </p>
                
                {isSubscribed ? (
                  <div className="flex items-center gap-2 text-green-600">
                    <CheckCircle2 className="h-5 w-5" />
                    <span>You've been added to our waitlist! We'll notify you when BEAMEvaluate Pro launches.</span>
                  </div>
                ) : (
                  <form onSubmit={handleWaitlistSubmit} className="flex gap-2">
                    <Input
                      type="email"
                      placeholder="Your email address"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="flex-1"
                      required
                      disabled={isSubmitting}
                    />
                    <Button 
                      type="submit" 
                      className="bg-blue-700 hover:bg-blue-800"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        "Join Waitlist"
                      )}
                    </Button>
                  </form>
                )}
              </div>
            </div>
            
            <div className="mt-4 text-xs text-gray-600 p-3 bg-white/60 rounded-md">
              <p>BEAMEvaluate Professional Edition combines cutting-edge artificial intelligence with blockchain technology 
              to deliver the most comprehensive and secure brand evaluation platform available. Our AI analyzes millions of data points 
              to provide actionable insights, while blockchain ensures the integrity and immutability of your brand's 
              emotional equity records.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ProfessionalDashboard;
